import React, { Fragment, useEffect, useRef, useState } from "react";
import { Alert, Grid } from "@mui/material";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { ArrowBack, AddPhotoAlternate, CloudUpload } from "@mui/icons-material";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { uploadImage } from "../../utils/awsUtils";
import { useUser } from "../context/UserContext";

const CreateGallery = () => {
  const [title, setTitle] = useState("");
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fileRef = useRef();
  const navigate = useNavigate();
  const { userRole, loading: roleLoading } = useUser();

  useEffect(() => {
    if (!roleLoading && userRole === "operator") {
      navigate("/home");
    }
  }, [roleLoading, userRole, navigate]);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const oversized = files.filter((f) => f.size > 2 * 1024 * 1024);
    if (oversized.length > 0) {
      setErrorMsg(`${oversized.length} file(s) exceed 2MB limit`);
      return;
    }

    setImageFiles(files);
    setImagePreviews(files.map((f) => URL.createObjectURL(f)));
    setErrorMsg("");
  };

  const removeImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGallery = async () => {
    if (!title) return setErrorMsg("Title cannot be empty");
    if (imageFiles.length === 0) return setErrorMsg("Please select at least one image");

    setLoading(true);
    setUploadProgress(0);
    try {
      for (let i = 0; i < imageFiles.length; i++) {
        const { imgUrl, imgId } = await uploadImage(imageFiles[i]);
        await addDoc(collection(db, "gallery"), {
          title,
          imgSrc: imgUrl,
          imgId,
          createdAt: serverTimestamp(),
        });
        setUploadProgress(Math.round(((i + 1) / imageFiles.length) * 100));
      }

      const userName = auth.currentUser?.email || "Admin";
      await addDoc(collection(db, "notifications"), {
        name: userName,
        message: `Added ${imageFiles.length} image(s) to gallery: ${title}`,
        createdAt: serverTimestamp(),
      });

      toast(`${imageFiles.length} image(s) added to gallery!`, { type: "success" });
      setTimeout(() => navigate("/gallery"), 2000);
    } catch (err) {
      setErrorMsg(err.message || "Failed to upload images");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <Fragment>
      <div className="min-h-screen bg-[#f5f7fe] p-3 sm:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => navigate("/gallery")}
              className="p-2 rounded-xl hover:bg-white border border-transparent hover:border-gray-100 transition"
            >
              <ArrowBack className="text-gray-400" style={{ fontSize: 20 }} />
            </button>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-[#000C66] font-aref">Add to Gallery</h1>
              <p className="text-gray-400 text-xs font-abel mt-0.5">Upload one or multiple images</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-8">
            {errorMsg && <Alert severity="error" className="mb-5" onClose={() => setErrorMsg("")}>{errorMsg}</Alert>}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">Gallery Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setErrorMsg(""); }}
                  placeholder="e.g., Sunday Service — June 2025"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl font-abel text-sm text-gray-700 focus:outline-none focus:border-[#000C66] focus:ring-2 focus:ring-[#000C66]/10 transition"
                />
              </Grid>

              <Grid item xs={12}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 font-abel">
                  Images * <span className="text-gray-300 normal-case">(each max 2MB, multiple allowed)</span>
                </label>

                {/* Upload zone */}
                <div
                  className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center cursor-pointer hover:border-[#000C66] hover:bg-[#f8faff] transition"
                  onClick={() => fileRef.current.click()}
                >
                  <CloudUpload className="text-gray-300 mx-auto mb-2" style={{ fontSize: 40 }} />
                  <p className="text-sm font-semibold text-gray-400 font-abel">
                    Click to select images
                  </p>
                  <p className="text-xs text-gray-300 font-abel mt-1">
                    You can select multiple files at once
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={handleImageSelect}
                />

                {/* Image previews */}
                {imagePreviews.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                    {imagePreviews.map((src, i) => (
                      <div key={i} className="relative group">
                        <img
                          src={src}
                          alt={`preview-${i}`}
                          className="w-full aspect-square object-cover rounded-xl border border-gray-100"
                        />
                        <button
                          onClick={() => removeImage(i)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition hover:bg-red-600"
                        >
                          ✕
                        </button>
                        <p className="text-xs text-gray-400 font-abel mt-1 truncate">{imageFiles[i]?.name}</p>
                      </div>
                    ))}
                  </div>
                )}

                {imageFiles.length > 0 && (
                  <p className="text-xs text-[#000C66] font-abel mt-3 font-semibold">
                    {imageFiles.length} image(s) selected
                  </p>
                )}
              </Grid>

              {loading && uploadProgress > 0 && (
                <Grid item xs={12}>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-[#000C66] h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 font-abel mt-1">Uploading {uploadProgress}%...</p>
                </Grid>
              )}

              <Grid item xs={12} className="flex justify-end">
                <button
                  onClick={handleGallery}
                  disabled={loading}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-[#000C66] text-white px-8 py-3 rounded-xl font-abel font-semibold hover:bg-blue-900 transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <AddPhotoAlternate style={{ fontSize: 18 }} />
                  )}
                  {loading ? "Uploading..." : `Upload ${imageFiles.length || ""} Image(s)`}
                </button>
              </Grid>
            </Grid>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default CreateGallery;
